import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { getIndustriesByGroup, OTHER_INDUSTRY_ID } from '../lib/industries';
import { Icon } from '../components/Icon';

export function OnboardingPage() {
    const { user } = useAuth();
    const { org, loading: orgLoading, createOrg } = useCurrentOrg();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [industry, setIndustry] = useState<string>('construction');
    const [industryCustom, setIndustryCustom] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Si l'user a déjà une org, on le renvoie direct sur le dashboard.
    useEffect(() => {
        if (!orgLoading && org) {
            navigate('/dashboard', { replace: true });
        }
    }, [orgLoading, org, navigate]);

    const isOther = industry === OTHER_INDUSTRY_ID;
    const customMissing = isOther && !industryCustom.trim();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!name.trim() || customMissing) return;
        setError(null);
        setSubmitting(true);
        try {
            await createOrg({
                name: name.trim(),
                industry,
                industryCustom: isOther ? industryCustom : undefined,
            });
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError((err as Error).message || 'Impossible de créer votre espace. Réessaie.');
            setSubmitting(false);
        }
    };

    const displayName = user?.displayName || user?.email?.split('@')[0] || 'vous';

    return (
        <div className="onboarding-page">
            <div className="onboarding-card">
                <a href="/" className="onboarding-brand" aria-label="Quotio">
                    <img src="/logoQuotio.png" alt="Quotio" />
                </a>

                <header className="onboarding-head">
                    <span className="onboarding-eyebrow">Bienvenue {displayName}</span>
                    <h1>Créons votre espace</h1>
                    <p>
                        Un espace = une entreprise. Vous pourrez ensuite créer vos
                        générateurs de devis et gérer les demandes reçues.
                    </p>
                </header>

                <form className="onboarding-form" onSubmit={handleSubmit}>
                    <label className="field">
                        <span className="field-label">Nom de votre entreprise</span>
                        <input
                            type="text"
                            required
                            autoFocus
                            maxLength={80}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="DB-BAT, Climatisation Dupont, etc."
                        />
                    </label>

                    <div className="field">
                        <span className="field-label">Secteur d'activité</span>
                        <div className="industry-groups" role="radiogroup">
                            {getIndustriesByGroup().map(({ group, items }) => (
                                <div key={group.id} className="industry-group">
                                    <div className="industry-group-title">{group.label}</div>
                                    <div className="industry-grid">
                                        {items.map((i) => (
                                            <label
                                                key={i.id}
                                                className={`industry-chip${industry === i.id ? ' is-active' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="industry"
                                                    value={i.id}
                                                    checked={industry === i.id}
                                                    onChange={() => setIndustry(i.id)}
                                                />
                                                <span className="industry-icon" aria-hidden>
                                                    <Icon name={i.icon} size={20} />
                                                </span>
                                                <span className="industry-label">{i.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {isOther && (
                            <label className="industry-custom">
                                <span className="field-label">Précisez votre secteur</span>
                                <input
                                    type="text"
                                    required
                                    maxLength={60}
                                    value={industryCustom}
                                    onChange={(e) => setIndustryCustom(e.target.value)}
                                    placeholder="Agence immobilière, Serrurerie, Coursier…"
                                />
                            </label>
                        )}
                    </div>

                    {error && <div className="auth-error" role="alert">{error}</div>}

                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={submitting || !name.trim() || customMissing}
                    >
                        {submitting ? 'Création…' : 'Créer mon espace'}
                    </button>
                </form>
            </div>
        </div>
    );
}
